import React from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import PageHeader from '../../components/account/PageHeader';
import Button from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import Icon from '../../components/ui/Icon';
import Text from '../../components/ui/Text';
import {
  categoryIcon,
  statusLabel,
  Ticket,
  TicketStatus,
} from '../../data/support';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  shadows,
  spacing,
  useResponsive,
} from '../../theme';

/**
 * Everything you have asked us about, in one list.
 *
 * Billing questions and disputes together rather than in two places: nobody
 * remembers which kind of problem they reported, and a customer looking for
 * "the thing about the bathroom" should not have to guess which tab it was
 * filed under. The category chip on each row is what tells them apart.
 *
 * Ordered by what moved last, not by what was raised last - an old ticket that
 * ops replied to this morning is the one being looked for.
 */

type Props = {
  tickets: Ticket[];
  onBack: () => void;
  onOpen: (ticket: Ticket) => void;
  onNew: () => void;
};

/**
 * Status as a chip.
 *
 * `rejected` reads "Not upheld" and is drawn in the plain ink rather than the
 * danger red - a decision that went against you is not an error, and colouring
 * it like one makes the app look as though something broke.
 */
function StatusChip({ status }: { status: TicketStatus }) {
  const settled = status === 'resolved';
  return (
    <View
      style={[
        styles.chip,
        settled ? styles.chipDone : null,
        status === 'in_review' ? styles.chipLive : null,
      ]}
    >
      <Text
        variant="caption"
        color={settled ? colors.onPrimary : colors.inkBody}
      >
        {statusLabel[status]}
      </Text>
    </View>
  );
}

function TicketsScreen({ tickets, onBack, onOpen, onNew }: Props) {
  const resp = useResponsive();

  const ordered = [...tickets].sort((a, b) =>
    a.status === b.status ? 0 : a.status === 'open' || a.status === 'in_review' ? -1 : 1,
  );

  return (
    <View style={styles.root}>
      <PageHeader title="My Requests" onBack={onBack} />

      <ScrollView
        style={styles.scroller}
        contentContainerStyle={[styles.page, resp.page]}
        showsVerticalScrollIndicator={false}
      >
        {ordered.length === 0 ? (
          <EmptyState
            icon="headset"
            title="Nothing raised yet"
            message="When you ask us about a charge or a job, it appears here with its status."
          />
        ) : (
          ordered.map(ticket => (
            <Pressable
              key={ticket.id}
              accessibilityRole="button"
              accessibilityLabel={`${ticket.subject}. ${statusLabel[ticket.status]}. Updated ${ticket.updatedOn}`}
              onPress={() => onOpen(ticket)}
              style={({ pressed }) => [
                styles.card,
                pressed ? styles.pressed : null,
              ]}
            >
              <View style={styles.mark}>
                <Icon
                  name={categoryIcon(ticket.category)}
                  size={19}
                  color={colors.primary}
                />
              </View>

              <View style={styles.body}>
                <View style={styles.head}>
                  <Text variant="subtitle" numberOfLines={1} style={styles.grow}>
                    {ticket.subject}
                  </Text>
                  <StatusChip status={ticket.status} />
                </View>

                <Text variant="caption" color={colors.inkMuted}>
                  {ticket.reference}
                  {ticket.bookingRef ? ` · ${ticket.bookingRef}` : ''}
                </Text>

                {/* The last thing said, so the list answers "any news?"
                    without opening anything. */}
                <Text variant="caption" color={colors.inkBody} numberOfLines={2}>
                  {ticket.messages[ticket.messages.length - 1]?.body}
                </Text>

                <Text variant="caption" color={colors.inkSubtle}>
                  Updated {ticket.updatedOn}
                </Text>
              </View>
            </Pressable>
          ))
        )}

        <Button label="Raise a new request" variant="secondary" onPress={onNew} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroller: {
    flex: 1,
  },
  page: {
    /* Vertical only: the horizontal gutter comes from `resp.page` on the
       contentContainer, which also carries the tablet inset. `padding`
       here was silently overridden by it. */
    paddingVertical: screenPadding,
    gap: spacing.md,
    paddingBottom: 120,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  mark: {
    width: 42,
    height: 42,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  body: {
    flex: 1,
    gap: 4,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  chip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceMuted,
    ...hairline,
  },
  chipLive: {
    borderColor: colors.primary,
  },
  chipDone: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  grow: {
    flex: 1,
  },
  pressed: {
    opacity: 0.7,
  },
});

export default TicketsScreen;
